import { Body } from "@sprout/typography";
import useTranslation from "@hooks/use_translation";
import { stageName } from "@utils/stage_name";
import type Application from "@/types/application";
import type { Stage } from "@/types/application";

const SEGMENT_COLORS = {
  complete: "bg-[var(--green-500)]",
  current: "bg-[var(--green-700)]",
  upcoming: "bg-[var(--evergreen-e25)]",
};

type SegmentState = keyof typeof SEGMENT_COLORS;

const segmentState = (index: number, position: number): SegmentState =>
  index < position - 1 ? "complete" : index === position - 1 ? "current" : "upcoming";

export const stageProgressFor = ({ stages }: Pick<Application, "stages">) => {
  if (!stages?.length) return null;

  const index = stages.findIndex((stage) => stage.status === "current");
  if (index === -1) return null;

  return { stage: stages[index], position: index + 1, total: stages.length };
};

type Props = {
  stage: Stage;
  position: number;
  total: number;
};

const StageProgress = ({ stage, position, total }: Props) => {
  const { t } = useTranslation("applications");

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex gap-1 h-1.5 w-full" aria-hidden="true">
        {Array.from({ length: total }, (_, index) => {
          const state = segmentState(index, position);
          return (
            <span
              key={index}
              data-state={state}
              className={`flex-1 min-w-px rounded-[3px] ${SEGMENT_COLORS[state]}`}
            />
          );
        })}
      </div>
      <div className="flex items-center justify-between gap-2 w-full">
        <Body as="span" secondary className="!text-[var(--dark-green)] truncate">
          {stageName(stage, t)}
        </Body>
        <Body as="span" secondary metadata className="shrink-0">
          {t("stage_progress", { position, total })}
        </Body>
      </div>
    </div>
  );
};

export default StageProgress;
